import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, verify2FA, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();

const handleLogin = async (e: React.FormEvent) => {
  e.preventDefault();
  setError('');
  setLoading(true);

  try {
    const result = await login(email, password);

    if (result.requires2FA) {
      setRequires2FA(true);
    } else {
      navigate('/dashboard');
    }
  } catch (err: any) {
    setError(err.response?.data?.detail || 'Login failed. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const handle2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verify2FA(email, twoFactorCode);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || '2FA verification failed.');
    } finally {
      setLoading(false);
    }
  };

const handleMicrosoftLogin = async () => {
  console.log('🔵 Microsoft login button clicked');
  setError('');
  setLoading(true);
  
  try {
    console.log('🔵 Calling loginWithMicrosoft...');
    await loginWithMicrosoft();
    console.log('🔵 loginWithMicrosoft completed');
  } catch (err: any) {
    console.error('🔴 Microsoft login error:', err);
    setError('Microsoft login failed: ' + err.message);
    setLoading(false);
  }
};

  if (requires2FA) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>Two-Factor Authentication</h1>
          <p>Enter the code from your authenticator app</p>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handle2FAVerification}>
            <div className="form-group">
              <label htmlFor="2fa-code">Authentication Code</label>
              <input
                type="text"
                id="2fa-code"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
                autoFocus
              />
            </div>
            
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
          
          <button 
            onClick={() => setRequires2FA(false)} 
            className="btn-link"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Azure Demo Platform</h1>
        <p className="subtitle">Sign in to your account</p>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className="divider">
          <span>OR</span>
        </div>
        
        <button 
          onClick={handleMicrosoftLogin} 
          className="btn-microsoft"
          type="button"
        >
          <svg className="microsoft-icon" viewBox="0 0 21 21">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          Sign in with Microsoft
        </button>
        
        <p className="signup-link">
          Don't have an account? <Link to="/register">Sign up</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
